import { useAuth } from "@/hooks/useAuth";
import { usePassRedeemStorage } from "@/hooks/usePassRedeemStorage";
import { fetchPassRedeem } from "@/services/passredeeem.queries";
import { useToastNotify } from "@/stores/toastStore";
import { useQuery } from "@tanstack/react-query";
import { t } from "@transifex/native";
import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { useAccount } from "./useAccount";

export function usePassRedeem(enabled: boolean, passID: string, termsAndConditionsOKTimestamp: string) {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { account, isFetching: isAccountFetching } = useAccount();
  const { notify } = useToastNotify();
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [errorNotified, setErrorNotified] = useState(false);

  const { alreadyRedeemed, hasLoaded, lastPassRedeem, saveLastPassRedeem } = usePassRedeemStorage(termsAndConditionsOKTimestamp);

  useEffect(() => {
    if (account?.phone) {
      setPhoneNumber(account.phone);
    }
  }, [account]);

  const { data, isPending, isError, error } = useQuery({
    enabled: enabled && hasLoaded && !alreadyRedeemed && !!passID && !isAuthLoading && !isAccountFetching,
    queryKey: ["pass-redeem", passID, termsAndConditionsOKTimestamp, phoneNumber],
    queryFn: () =>
      fetchPassRedeem({
        cognitoIDToken: user?.idToken ?? "",
        passID,
        termsAndConditionsOKTimestamp,
        phoneNumber: phoneNumber,
        // email: email ?? null,
      }),
    retry: false,
  });

  useFocusEffect(
    useCallback(() => {
      if (data) {
        saveLastPassRedeem({
          passRedeemResponse: data,
          termsAndConditionsOKTimestamp,
        });
      }
    }, [data, saveLastPassRedeem, termsAndConditionsOKTimestamp]),
  );

  useEffect(() => {
    if (isError && !errorNotified) {
      notify({
        severity: "error",
        message: t("An error occurred when redeeming the pass"),
      });
      console.error("Error redeeming pass:", error);
      setErrorNotified(true);
    }
    if (!isError) {
      setErrorNotified(false);
    }
  }, [isError, error, errorNotified, notify]);

  return {
    data: alreadyRedeemed ? lastPassRedeem?.passRedeemResponse : data,
    isPending: !(hasLoaded || !isPending),
    phoneNumber,
  };
}
